import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Badge,
  Box,
  ButtonSelect,
  Icon,
  InputField,
  MenuMulti,
  MobileDrawerItem,
  Typography,
} from '@saviynt/design-system';
import PropTypes from 'prop-types';

import { setIsBulkActionsCompleted } from '../../../redux/certdetails/action';
import { useInvertedLocalize } from '../../../utils/localization';

import { invertedMessages } from './messages';

function CustomChipFilter({
  filter,
  handleRemoveFilter,
  setPersonalizationStatus,
  handleMultiMenuChange,
  countOfCheckedOptions,
  isMobileResponsivenessEnabled,
}) {
  const dispatch = useDispatch();
  const l = useInvertedLocalize(invertedMessages);

  const [isExpanded, setIsExpanded] = useState(false);
  const menuMultiRef = useRef(null);
  const chipWrapperRef = useRef(null);

  // Close dropdown when focus leaves the chip via Tab key navigation
  useEffect(() => {
    if (!isExpanded) return () => {};

    const wrapper = chipWrapperRef.current;

    if (!wrapper) return () => {};

    const handleFocusOut = (event) => {
      // relatedTarget is the element receiving focus.
      // null means a non-focusable element was clicked — ignore (handled by MenuMulti's outside click).
      if (!event.relatedTarget) return;

      if (!wrapper.contains(event.relatedTarget)) {
        if (menuMultiRef.current) {
          menuMultiRef.current.closeDropdown();
        }
      }
    };

    wrapper.addEventListener('focusout', handleFocusOut);

    return () => wrapper.removeEventListener('focusout', handleFocusOut);
  }, [isExpanded]);

  // For the devices below than 600px width
  const [searchTextWhenMobile, setSearchTextWhenMobile] = useState('');
  const [mobileFilter, setMobileFilter] = useState(filter);

  useEffect(() => {
    setMobileFilter(filter);
  }, [filter]);

  const handleChangeForMobile = (optionValueKey) => {
    setMobileFilter((prev) => ({
      ...prev,
      options: prev.options.map((option) =>
        option.value === optionValueKey
          ? { ...option, isChecked: !option.isChecked }
          : option
      ),
    }));
  };

  useEffect(() => {
    if (isMobileResponsivenessEnabled) {
      const selectedOptions =
        mobileFilter.options.filter((option) => option.isChecked) || [];

      handleMultiMenuChange(mobileFilter.key, selectedOptions);
      dispatch(setPersonalizationStatus('UPDATE_PERSONALIZATION'));
      dispatch(setIsBulkActionsCompleted(false));
    }
  }, [mobileFilter]);

  if (isMobileResponsivenessEnabled) {
    const sortedAndFilteredFilterOptions = mobileFilter?.options
      ?.sort((a, b) => b.isChecked - a.isChecked)
      ?.filter(
        (option) =>
          option.isChecked ||
          option.label
            .toLowerCase()
            .indexOf(searchTextWhenMobile.toLowerCase()) !== -1
      );

    return (
      <>
        <div className='AddFilterDropdown-dropDownSearchBar--mobileResponsive'>
          <InputField
            name='basic'
            placeholder='Search'
            kind='search'
            size='large'
            prefixIcon={
              <Icon kind='search' color='neutral-700' size='smallMedium' />
            }
            value={searchTextWhenMobile}
            setValue={setSearchTextWhenMobile}
            tabIndex={0}
          />
        </div>
        <div className='AdvancedFilters-filterDrawerStep2NormalFilterOptions'>
          {sortedAndFilteredFilterOptions?.map((option) => (
            <MobileDrawerItem
              key={option.value}
              option={{ ...option, isMulti: true }}
              onClick={() => {
                handleChangeForMobile(option.value);
              }}
            />
          ))}
          {sortedAndFilteredFilterOptions?.length === 0 && (
            <Typography
              kind='body3'
              className='AdvancedFilters-filterDrawerStep2NormalFilter--noDataScreen'>
              {l('No data available')}
            </Typography>
          )}
        </div>
      </>
    );
  }

  return (
    <Box tag='div' className='CustomChipFilter' ref={chipWrapperRef}>
      <MenuMulti
        ref={menuMultiRef}
        key={filter.key}
        options={filter.options}
        appendLabelsToButton={false}
        label={filter.label}
        onChange={(selectedOptions) => {
          handleMultiMenuChange(filter.key, selectedOptions);
          dispatch(setIsBulkActionsCompleted(false));
        }}
        trigger={
          <ButtonSelect
            kind='pill'
            label=''
            BadgeComp={
              countOfCheckedOptions && countOfCheckedOptions > 0 ? (
                <Badge kind='number' number={countOfCheckedOptions} />
              ) : null
            }
          />
        }
        minOptionsForFooter={0}
        minOptionsForSearch={0}
        sortOnCheck
        headerText={filter?.appliedTo}
        headerTextConfigs={{
          headerTextClassName: 'AdvancedFilters-filterBarMenuItemHeaderText',
          splitOnToken: true,
          token: ':',
          preTokenClassName:
            'AdvancedFilters-filterBarMenuItemHeaderTextPreToken',
          postTokenClassName:
            'AdvancedFilters-filterBarMenuItemHeaderTextPostToken',
        }}
        footerLink1Text=' '
        onFooterLink1Click={() => handleRemoveFilter(filter.key)}
        showOptionLabelOnHover
        showFooterLink1
        isExpanded={isExpanded}
        setIsExpanded={setIsExpanded}
      />
    </Box>
  );
}

CustomChipFilter.propTypes = {
  filter: PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    options: PropTypes.arrayOf(PropTypes.shape({})),
    appliedTo: PropTypes.string,
  }),
  handleRemoveFilter: PropTypes.func.isRequired,
  setPersonalizationStatus: PropTypes.func.isRequired,
  handleMultiMenuChange: PropTypes.func.isRequired,
  countOfCheckedOptions: PropTypes.number.isRequired,
  isMobileResponsivenessEnabled: PropTypes.bool,
};

CustomChipFilter.defaultProps = {
  filter: {},
  isMobileResponsivenessEnabled: false,
};

export default CustomChipFilter;
